import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getCateringOrderDetailsAsync } from "../../redux/features/cateringCustomer/cateringCustomerThunks";
import {
  selectCateringOrderDetails,
} from "../../redux/features/cateringCustomer/cateringCustomerSelectors";
import { selectCateringPackages, selectPendingPayment } from "../../redux/features/restaurants/restaurantSelectors";
import { 
  setCateringCartFilters, 
  addCateringCartPackage, 
  setCateringRestaurantId,
  clearCateringCart,
  clearCateringCartItems,
  addItemToCateringPackage,
  setCateringItemsToRestore,
  clearCateringItemsToRestore,
  setIsPastOrderRestoration,
  setModifyOrderMode,
  clearModifyOrderMode,
  setUnpaidOrderMode,
  clearUnpaidOrderMode,
  setReorderMode,
  clearReorderMode,
  setPastOrderAddressId,
  clearPastOrderAddressId,
  clearPendingPayment,
  setCateringDeliveryCharge
} from "../../redux/features/restaurants/restaurantSlice";
import { getCateringPackagesAsync, getRestaurantItemsCategoriesAsync, getCateringPackageDetailAsync } from "../../redux/features/restaurants/restaurantThunks";
import { setSelectedAddress, setCateringOrderDetails } from "../../redux/features/cateringCustomer/cateringCustomerSlice";
import { setCompanySelectedAddress } from "../../redux/features/company/companySlice";
import { selectServiceType } from "../../redux/features/customer/customerSelectors";
import { formatDateForInput } from "../../utils/common";
import { restoreOrder } from "../../utils/orderRestoration";
import WarningModal from "../modal";
import CateringOrderDetailsModal from "../modal/CateringOrderDetailsModal";
import PendingPaymentWarningModal, { usePendingPaymentCheck } from "../modal/PendingPaymentWarningModal";

const CateringPastOrderCard = ({
  restaurant,
  serviceItemName,
  date,
  orderStatus,
  notes,
  orderId,
  orderNo,
  totalPrice,
  currency = "KWD",
  // Additional props for navigation and cart restoration
  serviceItemId,
  restaurantId,
  noOfPax,
  serviceItemDate,
  serviceItemSlot,
  serviceItems = [],
  customerName,
  orderDate,
  // Props for modification tracking
  updatedByAgentId = "",
  updatedDatetime = "",
  // Payment status for identifying paid cancelled orders
  paymentStatus = "",
  // Combined order props
  isCombineOrder = "no",
  combineOrderId = null,
  combineOrders = [], // Array of combined packages passed from parent
}) => {
  const isReorderDisabled = [
    "Ready for Dispatch",
    "Preparing",
    "On the way",
    "Submitted",
    "Accepted",
  ].includes(orderStatus);
  
  // Check if this order can be reordered (paid but cancelled)
  const canReorder = (paymentStatus === "Paid" || orderStatus === "Paid") && orderStatus === "Canceled";
  
  // Check if this order can be modified (unpaid cancelled order - for upgrade/downgrade)
  const canModifyCancelled = orderStatus === "Canceled" && !canReorder;
  
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isLoadingModalDetails, setIsLoadingModalDetails] = useState(false);
  
  // Use the pending payment check hook
  const { checkAndProceed, showWarning: showPendingPaymentWarning, handleProceed: handleConfirmPendingPayment, handleCancel: handleCancelPendingPayment } = usePendingPaymentCheck();
  
  // Check if order has been modified
  const isModified = updatedByAgentId && updatedByAgentId.trim() !== "";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const cateringOrderDetails = useSelector(selectCateringOrderDetails);
  const cateringPackages = useSelector(selectCateringPackages);
  const serviceType = useSelector(selectServiceType);
  const pendingPayment = useSelector(selectPendingPayment);

  // Define button styles based on the status
  const getButtonStyle = (status) => {
    switch (status) {
      case "Submitted":
        return "bg-[#F7B500] text-white";
      case "Accepted":
        return "bg-[#02BBD5] text-white";
      case "Canceled":
        return "bg-[#E02020] text-white";
      case "Ready for Dispatch":
        return "bg-[#F7B500] text-white";
      case "Preparing":
        return "bg-[#0091FF] text-white";
      case "On the way":
        return "bg-[#6236FF] text-white";
      case "Delivered":
        return "bg-[#4E948A] text-white";
      default:
        return "bg-gray-400 text-black";
    }
  };

  const handleErrorClose = () => {
    setShowErrorModal(false);
    setIsLoadingOrderDetails(false);
  };

  const handleViewDetails = () => {
    // For "Pending Payment" orders, navigate to package detail with cart restoration
    if (orderStatus === "Pending Payment") {
      // First fetch the complete order details, then restore cart
      if (
        cateringOrderDetails &&
        Object.keys(cateringOrderDetails).length > 0 &&
        (cateringOrderDetails.id === orderId || cateringOrderDetails.order_no === orderNo)
      ) {
        handlePendingPaymentOrder();
      } else {
        // If we don't have order details yet, the useEffect will handle it after fetching
        console.log('Order details not available, will be handled after fetch');
      }
    } else if (orderStatus === "Paid") {
      // For paid orders, enable modify mode (date/time editable, PAX frozen, no add-ons)
      if (
        cateringOrderDetails &&
        Object.keys(cateringOrderDetails).length > 0 &&
        (cateringOrderDetails.id === orderId || cateringOrderDetails.order_no === orderNo)
      ) {
        handleModifyOrder();
      } else {
        // If we don't have order details yet, the useEffect will handle it after fetching
        console.log('Order details not available, will be handled after fetch');
      }
    } else if (orderStatus === "Submitted" || orderStatus === "Accepted" || orderStatus === "Booked waiting for payment") {
      // For unpaid orders (Submitted/Accepted/Booked waiting for payment), enable unpaid mode
      if (
        cateringOrderDetails &&
        Object.keys(cateringOrderDetails).length > 0 &&
        (cateringOrderDetails.id === orderId || cateringOrderDetails.order_no === orderNo)
      ) {
        handleUnpaidOrder(); // Unpaid order mode - can modify and send payment link
      } else {
        // If we don't have order details yet, the useEffect will handle it after fetching
        console.log('Order details not available, will be handled after fetch');
      }
    } else if (orderStatus !== "Canceled") {
      // For other non-canceled orders, continue payment (restore cart like pending payment)
      if (
        cateringOrderDetails &&
        Object.keys(cateringOrderDetails).length > 0 &&
        (cateringOrderDetails.id === orderId || cateringOrderDetails.order_no === orderNo)
      ) {
        handlePendingPaymentOrder(); // Same behavior as pending payment - restore cart
      } else {
        // If we don't have order details yet, the useEffect will handle it after fetching
        console.log('Order details not available, will be handled after fetch');
      }
    } else {
      // For canceled orders, show details or navigate to order details page
      if (cateringOrderDetails && Object.keys(cateringOrderDetails).length > 0) {
        console.log('Catering Order Details:', cateringOrderDetails);
        // TODO: Navigate to catering order details page when implemented
        // navigate("/catering-order-details");
      } else {
        setShowErrorModal(true);
      }
    }
  };

  const handleModifyOrder = async () => {
    try {
      console.log('Starting modify order mode with order details:', cateringOrderDetails);
      
      // Use the detailed order data from API
      const orderData = cateringOrderDetails;
      
      // Check if this is a combined order and get the combine_catering_order_id
      const isCombined = orderData.is_combine_order === "yes" || orderData.is_combined_order === "yes";
      const combineCateringOrderId = isCombined ? (orderData.combine_catering_order_id || orderData.combine_order_id || combineOrderId) : null;
      
      console.log('🔒 Modify order mode setup:', {
        orderId,
        isCombined,
        combineCateringOrderId
      });
      
      // Pass modify order data to handleOrderRestoration
      // It will set the flags AFTER clearing the cart
      // Parse the original total from order details
      const originalTotal = parseFloat(orderData.total_price || totalPrice || 0);
      
      await handleOrderRestoration(orderData, true, {
        modifyOrderPax: parseInt(orderData.no_of_pax || noOfPax),
        modifyOrderId: orderId,
        modifyOrderOriginalTotal: originalTotal,
        combineOrderId: combineCateringOrderId
      });
    } catch (error) {
      console.error('Error setting up modify order mode:', error);
      setShowErrorModal(true);
    }
  };

  const handleUnpaidOrder = async () => {
    try {
      console.log('Starting unpaid order mode with order details:', cateringOrderDetails);
      
      // Use the detailed order data from API
      const orderData = cateringOrderDetails;
      
      // Check if this is a combined order and get the combine_catering_order_id
      const isCombined = orderData.is_combine_order === "yes" || orderData.is_combined_order === "yes";
      const combineCateringOrderId = isCombined ? (orderData.combine_catering_order_id || orderData.combine_order_id || combineOrderId) : null;
      
      console.log('💳 Unpaid order mode setup:', {
        orderId,
        isCombined,
        combineCateringOrderId
      });
      
      // Pass unpaid order data to handleOrderRestoration
      await handleOrderRestoration(orderData, false, null, true, {
        unpaidOrderId: orderId,
        combineOrderId: combineCateringOrderId
      });
    } catch (error) {
      console.error('Error setting up unpaid order mode:', error);
      setShowErrorModal(true);
    }
  };

  const handlePendingPaymentOrder = async () => {
    try {
      console.log('Starting cart restoration with order details:', cateringOrderDetails);
      
      // Use the detailed order data from API
      const orderData = cateringOrderDetails;
      
      // Not in modify mode for pending payment orders (pass null for modifyModeData)
      // clearModifyOrderMode will be called automatically by clearCateringCart
      await handleOrderRestoration(orderData, false, null);
    } catch (error) {
      console.error('Error restoring pending payment order:', error);
      setShowErrorModal(true);
    }
  };

  const handleReorderOrder = async () => {
    try {
      console.log('Starting reorder mode with order details:', cateringOrderDetails);
      
      // Use the detailed order data from API
      const orderData = cateringOrderDetails;
      
      // Parse the original total from order details
      const originalTotal = parseFloat(orderData.order_total_price || totalPrice || 0);
      
      // Pass reorder order data to handleOrderRestoration
      await handleOrderRestoration(orderData, false, null, false, null, true, {
        reorderOrderId: orderId,
        reorderOriginalTotal: originalTotal
      });
    } catch (error) {
      console.error('Error setting up reorder mode:', error);
      setShowErrorModal(true);
    }
  };

  const handleModifyCancelledOrder = async () => {
    try {
      console.log('Starting modify cancelled order mode with order details:', cateringOrderDetails);
      
      // Use the detailed order data from API
      const orderData = cateringOrderDetails;
      
      // For cancelled orders, allow full modification (upgrade/downgrade)
      // Use reorder mode which allows changing everything
      const originalTotal = parseFloat(orderData.order_total_price || totalPrice || 0);
      
      await handleOrderRestoration(orderData, false, null, false, null, true, {
        reorderOrderId: orderId,
        reorderOriginalTotal: originalTotal
      });
    } catch (error) {
      console.error('Error setting up modify cancelled order mode:', error);
      setShowErrorModal(true);
    }
  };

  const handleOrderRestoration = async (orderData, isModifyMode, modifyModeData = null, isUnpaidMode = false, unpaidModeData = null, isReorderMode = false, reorderModeData = null) => {
    try {
      // Use the shared restoration function from orderRestoration.js
      const result = await restoreOrder({
        orderData,
        isModifyMode,
        modifyModeData,
        isUnpaidMode,
        unpaidModeData,
        isReorderMode,
        reorderModeData,
        dispatch,
        navigate,
        setLoadingState: setIsLoadingOrderDetails,
        actions: {
          setModifyOrderMode,
          setUnpaidOrderMode,
          setReorderMode,
          clearCateringItemsToRestore,
          clearCateringCartItems,
          setCateringCartFilters,
          setPastOrderAddressId,
          getCateringPackagesAsync,
          getRestaurantItemsCategoriesAsync,
          getCateringPackageDetailAsync,
          setCateringRestaurantId,
          addCateringCartPackage,
          addItemToCateringPackage,
          setCateringItemsToRestore,
          setIsPastOrderRestoration,
          setCateringDeliveryCharge
        },
        fullRestoration: true // Full restoration with items for past order card
      });
      
      if (!result.success) {
        console.error('Order restoration failed:', result.error);
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error in order restoration:', error);
      setIsLoadingOrderDetails(false);
      setShowErrorModal(true);
    }
  };

  // Monitor cateringOrderDetails changes when loading
  useEffect(() => {
    if (isLoadingOrderDetails) {
      if (
        cateringOrderDetails &&
        Object.keys(cateringOrderDetails).length > 0 &&
        cateringOrderDetails.order_no === orderNo
      ) {
        setIsLoadingOrderDetails(false);
        
        // Handle different order statuses appropriately
        // Check if this is a reorder scenario (paid cancelled order)
        if (canReorder) {
          console.log('🔄 Paid cancelled order - entering reorder mode');
          handleReorderOrder();
        } else if (canModifyCancelled) {
          console.log('✏️ Unpaid cancelled order - entering modify mode for upgrade/downgrade');
          handleModifyCancelledOrder();
        } else if (orderStatus === "Pending Payment") {
          handlePendingPaymentOrder();
        } else if (orderStatus === "Paid") {
          // Only paid orders should go to modify mode
          handleModifyOrder();
        } else if (orderStatus === "Submitted" || orderStatus === "Accepted" || orderStatus === "Booked waiting for payment") {
          // Unpaid orders (Submitted/Accepted/Booked waiting for payment) should go to unpaid mode
          handleUnpaidOrder();
        } else if (orderStatus !== "Canceled") {
          // Other non-canceled orders should continue payment (restore cart)
          handlePendingPaymentOrder();
        } else {
          handleViewDetails();
        }
      }
    }
  }, [cateringOrderDetails, isLoadingOrderDetails, orderStatus, orderId, orderNo, canReorder, canModifyCancelled]);

  const onViewDetailsClick = async () => {
    // Check for pending payment with API verification
    const canProceed = await checkAndProceed(async () => {
      try {
        setIsLoadingOrderDetails(true);
        // Clear any stale order details before fetching new ones to avoid mismatched restoration
        dispatch(setCateringOrderDetails(null));
        
        // For "Pending Payment" orders, we need to fetch complete order details first
        if (orderStatus === "Pending Payment") {
          console.log('Fetching complete order details for pending payment order:', orderId, 'isCombineOrder:', isCombineOrder);
          await dispatch(getCateringOrderDetailsAsync(orderId, isCombineOrder));
          // The useEffect will handle cart restoration after order details are loaded
        } else {
          // For other orders, fetch details for viewing
          console.log('Fetching order details for order:', orderId, 'isCombineOrder:', isCombineOrder);
          await dispatch(getCateringOrderDetailsAsync(orderId, isCombineOrder));
          // The useEffect above will handle the rest of the process when cateringOrderDetails updates
        }
      } catch (error) {
        console.error("Error fetching catering order details:", error);
        setIsLoadingOrderDetails(false);
        setShowErrorModal(true);
      }
    });
    
    // If canProceed is true, execute immediately; otherwise modal will show
    if (canProceed) {
      try {
        setIsLoadingOrderDetails(true);
        dispatch(setCateringOrderDetails(null));
        
        if (orderStatus === "Pending Payment") {
          console.log('Fetching complete order details for pending payment order:', orderId, 'isCombineOrder:', isCombineOrder);
          await dispatch(getCateringOrderDetailsAsync(orderId, isCombineOrder));
        } else {
          console.log('Fetching order details for order:', orderId, 'isCombineOrder:', isCombineOrder);
          await dispatch(getCateringOrderDetailsAsync(orderId, isCombineOrder));
        }
      } catch (error) {
        console.error("Error fetching catering order details:", error);
        setIsLoadingOrderDetails(false);
        setShowErrorModal(true);
      }
    }
  };

  const onReorderClick = async () => {
    // Check for pending payment with API verification
    const canProceed = await checkAndProceed(async () => {
      try {
        setIsLoadingOrderDetails(true);
        dispatch(setCateringOrderDetails(null));
        
        console.log('Fetching order details for reorder:', orderId, 'isCombineOrder:', isCombineOrder);
        await dispatch(getCateringOrderDetailsAsync(orderId, isCombineOrder));
        // The useEffect will handle reorder mode setup after order details are loaded
      } catch (error) {
        console.error("Error fetching catering order details for reorder:", error);
        setIsLoadingOrderDetails(false);
        setShowErrorModal(true);
      }
    });
    
    // If canProceed is true, execute immediately; otherwise modal will show
    if (canProceed) {
      try {
        setIsLoadingOrderDetails(true);
        dispatch(setCateringOrderDetails(null));
        
        console.log('Fetching order details for reorder:', orderId, 'isCombineOrder:', isCombineOrder);
        await dispatch(getCateringOrderDetailsAsync(orderId, isCombineOrder));
      } catch (error) {
        console.error("Error fetching catering order details for reorder:", error);
        setIsLoadingOrderDetails(false);
        setShowErrorModal(true);
      }
    }
  };

  const onModifyCancelledClick = async () => {
    // Check for pending payment with API verification
    const canProceed = await checkAndProceed(async () => {
      try {
        setIsLoadingOrderDetails(true);
        dispatch(setCateringOrderDetails(null));
        
        console.log('Fetching order details for modify cancelled order:', orderId, 'isCombineOrder:', isCombineOrder);
        await dispatch(getCateringOrderDetailsAsync(orderId, isCombineOrder));
        // The useEffect will handle modify cancelled order mode setup after order details are loaded
      } catch (error) {
        console.error("Error fetching catering order details for modify cancelled order:", error);
        setIsLoadingOrderDetails(false);
        setShowErrorModal(true);
      }
    });
    
    // If canProceed is true, execute immediately; otherwise modal will show
    if (canProceed) {
      try {
        setIsLoadingOrderDetails(true);
        dispatch(setCateringOrderDetails(null));
        
        console.log('Fetching order details for modify cancelled order:', orderId, 'isCombineOrder:', isCombineOrder);
        await dispatch(getCateringOrderDetailsAsync(orderId, isCombineOrder));
      } catch (error) {
        console.error("Error fetching catering order details for modify cancelled order:", error);
        setIsLoadingOrderDetails(false);
        setShowErrorModal(true);
      }
    }
  };


  const handleShowDetailsModal = async (e) => {
    // Prevent event bubbling and default behavior
    e.preventDefault();
    e.stopPropagation();
    
    // Fetch detailed order info if not already available
    if (!cateringOrderDetails || cateringOrderDetails.order_no !== orderNo) {
      try {
        setIsLoadingModalDetails(true);
        console.log('Fetching order details for modal. Order:', orderNo, 'isCombineOrder:', isCombineOrder);
        await dispatch(getCateringOrderDetailsAsync(orderId, isCombineOrder));
        setIsLoadingModalDetails(false);
      } catch (error) {
        console.error("Error fetching order details:", error);
        setIsLoadingModalDetails(false);
        setShowErrorModal(true);
        return;
      }
    }
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
  };

  // Prepare order details for modal
  const prepareOrderDetailsForModal = () => {
    // Use cateringOrderDetails if available (from API), otherwise use props
    const detailsSource = (cateringOrderDetails && cateringOrderDetails.order_no === orderNo) 
      ? cateringOrderDetails 
      : null;

    // Check if this is a combined order
    const isCombined = detailsSource?.is_combine_order === "yes" || detailsSource?.is_combined_order === "yes" || isCombineOrder === "yes";

    return {
      orderNo,
      restaurant,
      serviceItemName,
      customerName,
      orderDate: detailsSource?.order_date || orderDate,
      serviceItemDate: detailsSource?.service_item_date || serviceItemDate,
      serviceItemSlot: detailsSource?.service_item_slot || serviceItemSlot,
      noOfPax: detailsSource?.no_of_pax || noOfPax,
      orderStatus: detailsSource?.order_status || orderStatus,
      paymentMethod: detailsSource?.payment_method_en || detailsSource?.payment_method_ar || "",
      updatedByAgentId: detailsSource?.updated_by_agent_id || updatedByAgentId,
      updatedDatetime: detailsSource?.updated_datetime || updatedDatetime,
      serviceItems: detailsSource?.service_item || serviceItems,
      notes: detailsSource?.comments || notes,
      service_item_price: detailsSource?.service_item_price || totalPrice,
      order_total_price: detailsSource?.order_total_price || totalPrice,
      total_price: detailsSource?.order_total_price || totalPrice,
      currency: detailsSource?.currency || currency,
      deliveryCharges: detailsSource?.delivery_charge || "",
      discount_price: detailsSource?.discount_price || "",
      paymentProcessingFee: detailsSource?.payment_processing_fee || "",
      // Bilbayt information
      is_bilbayt: detailsSource?.is_bilbayt || 0,
      bilbayt_price: detailsSource?.bilbayt_price || "",
      bilbayt_discount: detailsSource?.bilbayt_discount || "",
      bilbayt_order_id: detailsSource?.bilbayt_order_id || "",
      // Customer contact info
      customerPhoneNumber: detailsSource?.customer_phone_number || "",
      customerPhoneCountryCode: detailsSource?.customer_phone_country_code || "",
      deliveryArea: detailsSource?.delivery_area || detailsSource?.user_address?.delivery_charge_area_name_en || "",
      // User type and address
      userType: detailsSource?.userType || "customer",
      addressDetails: detailsSource?.user_address ? {
        addressType: detailsSource.user_address.address_type,
        block: detailsSource.user_address.block,
        street: detailsSource.user_address.street,
        avenue: detailsSource.user_address.avenue,
        houseNo: detailsSource.user_address.house_no,
        building: detailsSource.user_address.building,
        floor: detailsSource.user_address.floor,
        apartmentNo: detailsSource.user_address.apartment_no,
        companyName: detailsSource.user_address.company_name,
        mobileNumber: detailsSource.user_address.phone_no,
        countryCode: detailsSource.user_address.phone_country_code,
        address1: detailsSource.user_address.address1,
        address2: detailsSource.user_address.address2,
      } : null,
      // Combined order specific fields
      isCombineOrder: isCombined,
      combineOrders: detailsSource?.combine_orders || [],
      combineOrderId: detailsSource?.combine_order_id || combineOrderId,
    };
  };

  // Check if this is a combined order and prepare display info
  const isCombined = isCombineOrder === "yes";
  
  // For combined orders, get info from props first (immediate display), then from cateringOrderDetails (after API call)
  const combineOrdersData = combineOrders.length > 0 
    ? combineOrders 
    : (cateringOrderDetails?.combine_orders || []);
    
  const packageCount = isCombined && combineOrdersData.length > 0 
    ? combineOrdersData.length 
    : (isCombined ? "Multiple" : null);

  return (
    <div className={`border-2 rounded-lg p-3 shadow-lg w-full h-auto ${
      isCombined ? 'border-purple-300 bg-purple-50' : 'border-slate-300 bg-neutral-50'
    }`}>
      {/* Header with improved wrapping */}
      <div className="flex flex-wrap justify-between items-start gap-2">
        {!isCombined ? (
          <h2 className="text-lg text-gray-500 font-semibold truncate max-w-[calc(100%-80px)]">
            {restaurant}
          </h2>
        ) : (
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-1 bg-purple-600 text-white text-xs font-bold rounded-full shadow">
                COMBINED ORDER
              </span>
            </div>
            <div className="text-base font-bold text-purple-800">
              {packageCount} Package{typeof packageCount === 'number' && packageCount !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>
      
      {/* Service item name or combined order packages */}
      <div className="text-sm text-gray-700 font-medium mt-2">
        {isCombined && combineOrdersData.length > 0 ? (
          <div className="space-y-1">
            {combineOrdersData.map((pkg, idx) => (
              <div key={idx} className="flex items-start gap-1 text-xs">
                <span className="text-purple-600 font-bold mt-0.5">•</span>
                <div className="flex-1">
                  <div className="font-semibold text-gray-700">
                    {pkg.service_item_name_en || pkg.service_item_name_ar}
                  </div>
                  <div className="text-gray-500">
                    {pkg.restaurant_name_en || pkg.restaurant_name_ar}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : !isCombined ? (
          serviceItemName
        ) : null}
      </div>
      
      <div className="text-xs text-gray-600 truncate mt-1">Order #: {orderNo}</div>
      {customerName && (
        <div className="text-xs text-gray-600 truncate">Customer: {customerName}</div>
      )}
      
      {/* Order dates section */}
      <div className="mt-2 space-y-0.5">
        {orderDate && (
          <div className="text-xs text-gray-500">
            <span className="font-medium">Placed:</span> {orderDate}
          </div>
        )}
        {!isCombined && serviceItemDate && (
          <div className="text-xs text-gray-500">
            <span className="font-medium">Event:</span> {serviceItemDate}
            {serviceItemSlot && <span className="ml-1">at {serviceItemSlot}</span>}
          </div>
        )}
        {isCombined && combineOrdersData.length > 0 && (
          <div className="text-xs text-gray-500">
            <span className="font-medium">Event:</span> {combineOrdersData[0].service_item_date}
            {combineOrdersData[0].service_item_slot && <span className="ml-1">at {combineOrdersData[0].service_item_slot}</span>}
          </div>
        )}
      </div>
      
      {/* Modified badge */}
      {isModified && (
        <div className="flex items-center gap-1 mt-2 p-1.5 bg-blue-50 rounded border border-blue-200">
          <svg className="w-3.5 h-3.5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
          <div className="flex flex-col">
            <span className="text-xs font-semibold text-blue-700">
              Modified by Agent #{updatedByAgentId}
            </span>
            {updatedDatetime && (
              <span className="text-xs text-blue-600">
                on {updatedDatetime}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Service Details */}
      {!isCombined && (
        <div className="mt-2 w-full">
          <div className="text-sm text-gray-500">
            {noOfPax} PAX • {serviceItemSlot}
          </div>
          {serviceItems.length > 0 && (
            <div className="text-xs text-gray-400 mt-1">
              {serviceItems.length} item{serviceItems.length !== 1 ? 's' : ''} selected
            </div>
          )}
        </div>
      )}
      
      {/* Combined Order Summary */}
      {isCombined && combineOrdersData.length > 0 && (
        <div className="mt-2 w-full">
          <div className="text-xs text-gray-600 space-y-1">
            {combineOrdersData.map((pkg, idx) => (
              <div key={idx} className="flex justify-between items-center">
                <span className="truncate flex-1">
                  <span className="font-semibold text-purple-700">{pkg.no_of_pax} PAX</span> - {pkg.service_item_name_en?.substring(0, 20) || pkg.service_item_name_ar?.substring(0, 20)}
                  {(pkg.service_item_name_en?.length > 20 || pkg.service_item_name_ar?.length > 20) && '...'}
                </span>
                <span className="ml-2 text-purple-600 font-medium whitespace-nowrap">
                  {currency} {parseFloat(pkg.service_item_price).toFixed(3)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Price Breakdown */}
      <hr style={{ marginTop: "8px", borderColor: "#959FA8" }} />
      
      {/* Get order details from Redux if available for accurate pricing */}
      {(() => {
        const detailsSource = (cateringOrderDetails && cateringOrderDetails.order_no === orderNo) 
          ? cateringOrderDetails 
          : null;
        
        const deliveryCharge = parseFloat(detailsSource?.delivery_charge || 0);
        const discountPrice = parseFloat(detailsSource?.discount_price || 0);
            
        // Calculate order total - handle empty string case for Bilbayt orders
        let orderTotal = parseFloat(detailsSource?.order_total_price || totalPrice || 0);
        
        // If total is 0 or NaN (empty string), calculate from combined orders or service_item_price
        if (!orderTotal || isNaN(orderTotal)) {
          const isCombined = detailsSource?.is_combine_order === "yes" || detailsSource?.is_combined_order === "yes" || isCombineOrder === "yes";
          
          if (isCombined && detailsSource?.combine_orders && detailsSource.combine_orders.length > 0) {
            // Sum up all package prices for combined orders
            orderTotal = detailsSource.combine_orders.reduce((sum, pkg) => {
              return sum + parseFloat(pkg.service_item_price || 0);
            }, 0);
          } else {
            // For single orders, use service_item_price
            orderTotal = parseFloat(detailsSource?.service_item_price || totalPrice || 0);
          }
          
          // Add delivery charges
          orderTotal = orderTotal + deliveryCharge;
        }
        
        const isBilbayt = detailsSource?.is_bilbayt === 1 || detailsSource?.is_bilbayt === true || orderStatus === "Bilbayt";
        const bilbaytPrice = parseFloat(detailsSource?.bilbayt_price || 0);
        const bilbaytDiscount = parseFloat(detailsSource?.bilbayt_discount || 0);
        const bilbaytOrderId = detailsSource?.bilbayt_order_id || "";
        
        // Calculate subtotal (total - delivery charge)
        const subtotal = orderTotal - deliveryCharge;
        
        return (
          <div className="mt-1 space-y-1">
            {/* Subtotal */}
            {subtotal > 0 && (
              <div className="flex justify-between text-xs text-gray-600">
                <span>Subtotal</span>
                <span>{currency} {subtotal.toFixed(3)}</span>
              </div>
            )}
            
            {/* Delivery Charges */}
            {deliveryCharge > 0 && (
              <div className="flex justify-between text-xs text-gray-600">
                <span>Delivery Charges</span>
                <span>{currency} {deliveryCharge.toFixed(3)}</span>
              </div>
            )}
            
            {/* Discount */}
            {discountPrice > 0 && (
              <div className="flex justify-between text-xs text-red-600">
                <span>Discount</span>
                <span>-{currency} {discountPrice.toFixed(3)}</span>
              </div>
            )}
            
            {/* Bilbayt Information */}
            {isBilbayt && bilbaytPrice > 0 && (
              <>
                <div className="flex justify-between text-xs text-amber-700 font-semibold mt-2 pt-2 border-t border-amber-200">
                  <span className="flex items-center gap-1">
                    <img src="/Images/bilbayt.jpeg" alt="Bilbayt" className="w-4 h-4 object-contain" />
                    Bilbayt Payment
                  </span>
                  <span>{currency} {bilbaytPrice.toFixed(3)}</span>
                </div>
                {bilbaytDiscount > 0 && (
                  <div className="flex justify-between text-xs text-green-600">
                    <span>Bilbayt Discount</span>
                    <span>-{currency} {bilbaytDiscount.toFixed(3)}</span>
                  </div>
                )}
                {bilbaytOrderId && (
                  <div className="text-xs text-gray-500 italic">
                    Order ID: {bilbaytOrderId}
                  </div>
                )}
              </>
            )}
            
            {/* Total */}
            <div className="flex justify-between mt-2 pt-2 border-t border-gray-300">
              <span className="text-sm font-semibold text-gray-500">Total</span>
              <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                {currency} {orderTotal.toFixed(3)}
              </span>
            </div>
          </div>
        );
      })()}
      
      <hr style={{ marginTop: "5px", borderColor: "#959FA8" }} />
      
      {/* Notes with ellipsis for overflow */}
      <div className="block text-sm font-medium text-gray-500 mt-1 mb-3 truncate">
        Notes: {notes ? notes : "N/A"}
      </div>

      {/* Responsive Action Buttons */}
      <div className="flex flex-col w-full mt-2 gap-2">
        <div className="flex justify-between w-full gap-2 flex-wrap">
          {/* Status Button */}
          <button
            className={`${getButtonStyle(
              orderStatus
            )} text-xs font-bold rounded-md px-2 h-[21px] flex-shrink-0 text-center`}
          >
            {orderStatus}
          </button>

          {/* Action Button - different text based on order status, hidden for Accepted orders */}
          {orderStatus !== "Accepted" && (
            <button
              className={`text-xs font-bold rounded-md px-2 h-[21px] flex-shrink-0 text-center ${
                isLoadingOrderDetails
                  ? "bg-amber-50 text-white cursor-not-allowed"
                  : orderStatus === "Paid"
                  ? "bg-blue-500 text-white hover:bg-blue-600"
                  : orderStatus !== "Canceled"
                  ? "bg-orange-500 text-white hover:bg-orange-600"
                  : "bg-amber-100 text-white hover:bg-amber-200"
              }`}
              disabled={isLoadingOrderDetails}
              onClick={
                !isLoadingOrderDetails
                  ? onViewDetailsClick
                  : undefined
              }
            >
              {isLoadingOrderDetails 
                ? "Loading..." 
                : orderStatus === "Paid"
                ? "View or Modify"
                : orderStatus === "Submitted"
                ? "Modify & Send Payment"
                : orderStatus === "Booked waiting for payment"
                ? "Continue Order"
                : orderStatus !== "Canceled"
                ? "Continue Order"
                : "View Details"
              }
            </button>
          )}
        </div>
        
        {/* Reorder Button - for paid cancelled orders */}
        {canReorder && (
          <button
            onClick={onReorderClick}
            disabled={isLoadingOrderDetails}
            className={`w-full text-xs font-semibold rounded-md px-3 py-2 text-center border transition-colors ${
              isLoadingOrderDetails
                ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                : "bg-purple-600 text-white border-purple-600 hover:bg-purple-700 shadow-sm"
            }`}
          >
            <div className="flex items-center justify-center gap-1.5">
              {isLoadingOrderDetails ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>🔄 Reorder (Modify & Repay)</span>
                </>
              )}
            </div>
          </button>
        )}
        
        {/* Modify Order Button - for unpaid cancelled orders (upgrade/downgrade) */}
        {canModifyCancelled && (
          <button
            onClick={onModifyCancelledClick}
            disabled={isLoadingOrderDetails}
            className={`w-full text-xs font-semibold rounded-md px-3 py-2 text-center border transition-colors ${
              isLoadingOrderDetails
                ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                : "bg-green-600 text-white border-green-600 hover:bg-green-700 shadow-sm"
            }`}
          >
            <div className="flex items-center justify-center gap-1.5">
              {isLoadingOrderDetails ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span>✏️ Modify Order (Upgrade/Downgrade)</span>
                </>
              )}
            </div>
          </button>
        )}
        
        {/* Full Details Button */}
        <button
          onClick={handleShowDetailsModal}
          disabled={isLoadingModalDetails || isLoadingOrderDetails}
          className={`w-full text-xs font-semibold rounded-md px-3 py-2 text-center border transition-colors ${
            isLoadingModalDetails || isLoadingOrderDetails
              ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
              : "bg-slate-700 text-white border-slate-700 hover:bg-slate-800 shadow-sm"
          }`}
        >
          <div className="flex items-center justify-center gap-1.5">
            {isLoadingModalDetails ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Loading...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>View Full Order Details</span>
              </>
            )}
          </div>
        </button>
      </div>

      {/* Error Modal */}
      {showErrorModal && (
        <WarningModal
          onCancel={handleErrorClose}
          onProceed={handleErrorClose}
          description="Unable to load order details"
          subDescription="The catering order details could not be retrieved. Please try again later."
          confirmButtonText="OK"
          showCancelButton={false}
          showItems={false}
        />
      )}

      {/* Pending Payment Warning Modal */}
      <PendingPaymentWarningModal
        isOpen={showPendingPaymentWarning}
        onClose={handleCancelPendingPayment}
        onProceed={handleConfirmPendingPayment}
        actionDescription={`viewing this order (Order #${orderNo})`}
      />

      {/* Order Details Modal */}
      <CateringOrderDetailsModal
        isOpen={showDetailsModal}
        onClose={handleCloseDetailsModal}
        orderDetails={prepareOrderDetailsForModal()}
      />
    </div>
  );
};

// PropTypes validation
CateringPastOrderCard.propTypes = {
  restaurant: PropTypes.string.isRequired,
  serviceItemName: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  orderStatus: PropTypes.string.isRequired,
  notes: PropTypes.string,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  orderNo: PropTypes.string.isRequired,
  totalPrice: PropTypes.string.isRequired,
  currency: PropTypes.string,
  // Additional props for navigation and cart restoration
  serviceItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  restaurantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  noOfPax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serviceItemDate: PropTypes.string,
  serviceItemSlot: PropTypes.string,
  serviceItems: PropTypes.array,
  customerName: PropTypes.string,
  orderDate: PropTypes.string,
  // Props for modification tracking
  updatedByAgentId: PropTypes.string,
  updatedDatetime: PropTypes.string,
  paymentStatus: PropTypes.string,
  // Combined order props
  isCombineOrder: PropTypes.string,
  combineOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  combineOrders: PropTypes.array,
};

CateringPastOrderCard.defaultProps = {
  notes: "",
  currency: "KWD",
  serviceItems: [],
  customerName: "",
  orderDate: "",
  updatedByAgentId: "",
  updatedDatetime: "",
  paymentStatus: "",
  isCombineOrder: "no",
  combineOrderId: null,
  combineOrders: [],
};

export default CateringPastOrderCard;
